/**
 * Unified-runtime flow registry.
 *
 * Skillflows-prune Phase 1.A.4: each entry below is a coagent flow that has
 * been migrated to `AgentRuntimeSession` storage and uses the new
 * `/core/agent/*` endpoints (start, resume, save-ui-state) instead of the
 * legacy `/core/coagent/*` ones (init, resume, save-frontend-state).
 *
 * Adding an entry here flips a flow over — every call site that branches on
 * `isUnifiedRuntimeFlow(...)` picks up the new path automatically.
 *
 * Entries hold the agent registration metadata the new endpoints need:
 *  - `agentName` — the `AgentDefinitionVersion.agent_name` for this flow's
 *    orchestrator. Passed to `POST /core/agent/start`.
 *  - `wizard` — the `?wizard=<kind>` opt-in for `GET /core/agent/resume` so
 *    the response carries the artifact-derived `campaignData` /
 *    `stepStatuses` / `artifacts`.
 */

export interface UnifiedFlowConfig {
  /** AgentDefinitionVersion.agent_name — the orchestrator that runs this flow. */
  agentName: string;
  /**
   * Pipeline kind that `/core/agent/resume?wizard=<kind>` knows how to
   * hydrate. Currently constrained to `CoAgentPipelineType` server-side.
   */
  wizard: string;
}

export const UNIFIED_RUNTIME_FLOWS: Record<string, UnifiedFlowConfig> = {
  // Phase 1.D — Saga / social-studio. The agent-runtime social_*
  // tools were already workspace-keyed (ctx.workspaceId) so this
  // migration is just frontend init + chat orchestrator routing.
  // useSagaSession resolves the AgentRuntimeSession via the shared
  // ensureRuntimeSession helper.
  "social-studio": { agentName: "saga", wizard: "social-studio" },
  // Phase 1.E — Nova / content-studio. Collapses Nova's prior
  // multi-session-per-workspace UX into the unified one-session-per-
  // workspace invariant (same as forge / saga / brand-dna). The
  // legacy `/core/coagent/init` entry resolved coAgentType=content to
  // workflow_slug='social-copilot' (a stale pre-saga mapping); the
  // unified path routes through agentName=nova which AGENT_TO_WORKFLOW_SLUG
  // now maps to the correct 'content-studio-copilot'.
  content: { agentName: "nova", wizard: "content" },
  // Phase 1.F — Marcus / Ads. The orchestrator persona on the
  // backend AgentDefinitionVersion is `marcus` (the `operator` AgentId
  // in the frontend's persona map is the legacy display label for the
  // growth-copilot workflow). Marcus's tool surface still loads via
  // `createCompanionToolRegistry` keyed on workflowSlug='growth-copilot'
  // because resolveAgentId('growth-copilot') === 'operator' isn't in
  // the FORCE_DB_PROMPT_AGENTS allowlist — only the session storage
  // flips here, not the chat routing.
  ads: { agentName: "marcus", wizard: "ads" },
};

export function isUnifiedRuntimeFlow(coAgentType: string | null | undefined): boolean {
  return !!coAgentType && coAgentType in UNIFIED_RUNTIME_FLOWS;
}

export function getUnifiedFlowConfig(
  coAgentType: string | null | undefined,
): UnifiedFlowConfig | null {
  if (!coAgentType) return null;
  return UNIFIED_RUNTIME_FLOWS[coAgentType] ?? null;
}
